// Prompt 0454 — Sub-modal para asignar persona a un puesto con combobox mejorado.
// M2.1: dropdown visible al hacer focus sin texto (muestra 50 iniciales por codigo ASC).

import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { crearPersonaAsignacion, fetchPersonas } from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';

interface Props {
  puestoId: string;
  puestoTitulo: string;
  personasExcluidas?: string[];
  onClose: () => void;
  onAssigned: () => void;
}

interface PersonaRow {
  id: string;
  nombre: string;
  apellidos: string;
  codigo: string;
}

export function AsignarPersonaSubPanel({
  puestoId,
  puestoTitulo,
  personasExcluidas = [],
  onClose,
  onAssigned,
}: Props) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;

  const [search, setSearch] = useState('');
  const [personasIniciales, setPersonasIniciales] = useState<PersonaRow[]>([]);
  const [resultados, setResultados] = useState<PersonaRow[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fte, setFte] = useState('1');
  const [esPrincipal, setEsPrincipal] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  // M2.1: precargar 50 personas iniciales ordenadas por codigo ASC al montar
  useEffect(() => {
    fetchPersonas({ porPagina: '50', orden: 'codigo' })
      .then((r) =>
        setPersonasIniciales(
          (r.data ?? []).map((p) => ({
            id: p.id,
            nombre: p.nombre,
            apellidos: p.apellidos,
            codigo: p.codigo,
          })),
        ),
      )
      .catch(() => setPersonasIniciales([]));
  }, []);

  // M2.1: busqueda server-side con debounce 300ms cuando hay >=2 chars
  useEffect(() => {
    if (search.length < 2) {
      setResultados([]);
      return;
    }
    const timer = setTimeout(() => {
      fetchPersonas({ q: search, porPagina: '50' })
        .then((r) =>
          setResultados(
            (r.data ?? []).map((p) => ({
              id: p.id,
              nombre: p.nombre,
              apellidos: p.apellidos,
              codigo: p.codigo,
            })),
          ),
        )
        .catch(() => setResultados([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ESC cierra el sub-modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Click fuera del dropdown cierra solo el dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const excludedSet = new Set(personasExcluidas);
  const listaVisible = (search.length >= 2 ? resultados : personasIniciales).filter(
    (p) => !excludedSet.has(p.id),
  );

  const handleAssign = async () => {
    if (!selectedPersona) {
      setError(panel.seleccionaPersona ?? 'Selecciona una persona');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await crearPersonaAsignacion(selectedPersona.id, {
        puestoId,
        fechaInicio,
        fte: parseFloat(fte) || 1,
        esPrincipal,
      });
      onAssigned();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0E243E' }}>
          {panel.asignarPersonaA ?? 'Asignar persona a'}: {puestoTitulo}
        </h3>

        <div style={{ marginBottom: 12 }} ref={containerRef}>
          <label style={subLabelStyle}>{panel.buscarPersona ?? 'Buscar persona'}</label>
          {selectedPersona ? (
            <div style={selectedChipStyle}>
              <span style={{ flex: 1, fontSize: 13 }}>{selectedPersona.label}</span>
              <button
                onClick={() => {
                  setSelectedPersona(null);
                  setSearch('');
                  setDropdownOpen(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#DC2626',
                  fontSize: 16,
                  fontFamily: 'inherit',
                }}
                aria-label="clear"
              >
                x
              </button>
            </div>
          ) : (
            <>
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                placeholder={panel.placeholderBuscar ?? 'Nombre o codigo...'}
                style={subInputStyle}
              />
              {dropdownOpen && listaVisible.length > 0 && (
                <div style={dropdownStyle}>
                  {listaVisible.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => {
                        setSelectedPersona({
                          id: r.id,
                          label: `${r.nombre} ${r.apellidos} (${r.codigo})`,
                        });
                        setResultados([]);
                        setDropdownOpen(false);
                        setSearch('');
                      }}
                      style={dropdownItemStyle}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F4FA')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                    >
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11,
                          color: '#5A6171',
                          marginRight: 8,
                          minWidth: 60,
                          display: 'inline-block',
                        }}
                      >
                        {r.codigo}
                      </span>
                      {r.nombre} {r.apellidos}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 16px',
            marginBottom: 12,
          }}
        >
          <div>
            <label style={subLabelStyle}>{panel.fechaInicio ?? 'Fecha inicio'} *</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              style={subInputStyle}
            />
          </div>
          <div>
            <label style={subLabelStyle}>{panel.fte ?? 'FTE (0-1)'}</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={fte}
              onChange={(e) => setFte(e.target.value)}
              style={subInputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={esPrincipal}
              onChange={(e) => setEsPrincipal(e.target.checked)}
            />
            {panel.esPrincipal ?? 'Es principal'}
          </label>
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>
            {panel.cancel ?? 'Cancelar'}
          </button>
          <button onClick={handleAssign} disabled={saving} style={btnPrimary(saving)}>
            {saving ? (panel.asignando ?? 'Asignando...') : (panel.asignar ?? 'Asignar')}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  borderTop: '3px solid #1F3864',
  maxWidth: 500,
  width: '95%',
  padding: '24px 28px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};

const subInputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const subLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  display: 'block',
  marginBottom: 4,
};

const selectedChipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  background: '#F0F4FA',
  borderRadius: 6,
  border: '1px solid #D1D5DB',
};

const dropdownStyle: React.CSSProperties = {
  border: '1px solid #E5E7EB',
  borderRadius: 6,
  marginTop: 4,
  maxHeight: 260,
  overflow: 'auto',
  background: '#fff',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
};

const dropdownItemStyle: React.CSSProperties = {
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 13,
  borderBottom: '1px solid #F3F4F6',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 20px',
  background: 'transparent',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: '#374151',
};

const btnPrimary = (saving: boolean): React.CSSProperties => ({
  padding: '8px 24px',
  background: '#2F5496',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: saving ? 'not-allowed' : 'pointer',
  opacity: saving ? 0.7 : 1,
  fontFamily: 'inherit',
});
